const DEFAULT_LOCALE = 'en-US';

type FormatNumberInputTextOptions = {
  integer?: boolean;
};

export const getNumberInputDisplayValue = (
  value: number | string | null | undefined,
  options: Intl.NumberFormatOptions = {}
): string => {
  if (value === null || value === undefined || value === '') {
    return '';
  }

  const numericValue = typeof value === 'number' ? value : parseFormattedNumber(value);

  if (!Number.isFinite(numericValue)) {
    return '';
  }

  return new Intl.NumberFormat(DEFAULT_LOCALE, {
    maximumFractionDigits: 20,
    ...options,
  }).format(numericValue);
};

export const formatAdminNumber = getNumberInputDisplayValue;

export const formatCompactAdminNumber = (value: number | null | undefined): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '0';
  }

  return new Intl.NumberFormat(DEFAULT_LOCALE, {
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value);
};

export const parseFormattedNumber = (value: string): number => {
  const normalizedValue = value.replace(/,/g, '').trim();

  if (!normalizedValue) {
    return 0;
  }

  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : 0;
};

export const parseFormattedInteger = (value: string): number => {
  return Math.floor(parseFormattedNumber(value));
};

export const formatNumberInputText = (
  value: string,
  options: FormatNumberInputTextOptions = {}
): string => {
  const normalizedValue = value.replace(/,/g, '').trim();

  if (!normalizedValue) {
    return '';
  }

  const sign = normalizedValue.startsWith('-') ? '-' : '';
  const unsignedValue = sign ? normalizedValue.slice(1) : normalizedValue;
  const [integerPart = '', ...decimalParts] = unsignedValue.split('.');
  const digitsOnlyInteger = integerPart.replace(/\D/g, '');
  const numericInteger = digitsOnlyInteger ? Number(digitsOnlyInteger) : 0;
  const formattedInteger = new Intl.NumberFormat(DEFAULT_LOCALE).format(numericInteger);

  if (options.integer) {
    return `${sign}${formattedInteger}`;
  }

  if (decimalParts.length === 0) {
    return `${sign}${formattedInteger}`;
  }

  const decimalPart = decimalParts.join('').replace(/\D/g, '');
  return `${sign}${formattedInteger}.${decimalPart}`;
};

export const parseOptionalFormattedNumber = (value: string): number | undefined => {
  const normalizedValue = value.replace(/,/g, '').trim();

  if (!normalizedValue) {
    return undefined;
  }

  const numericValue = Number(normalizedValue);
  return Number.isFinite(numericValue) ? numericValue : undefined;
};

export const parseOptionalFormattedInteger = (value: string): number | undefined => {
  const numericValue = parseOptionalFormattedNumber(value);
  return numericValue === undefined ? undefined : Math.floor(numericValue);
};

export const numberInputProps = {
  inputMode: 'numeric',
} as const;

export const decimalInputProps = {
  inputMode: 'decimal',
} as const;
